export interface Source {
  name: string
  url?: string
  quote?: string
  date?: string
}
export interface FactCheck {
  timestamp: number // seconds into video
  speaker: string
  claim: string
  rating: 'True' | 'False' | 'Misleading' | 'Unverified'
  explanation: string
  sources: Source[]
  category?: string // e.g., 'Economy', 'Healthcare', 'Immigration'
}
export const mockFactChecks: FactCheck[] = [
  {
    timestamp: 25,
    speaker: 'Candidate A',
    claim: 'Unemployment is at its lowest point in 50 years.',
    rating: 'Misleading',
    explanation:
      "While unemployment was indeed low before the pandemic, it had been at similar levels in the late 1960s. Additionally, this statement doesn't account for the significant job losses during the pandemic.",
    sources: [
      {
        name: 'Bureau of Labor Statistics',
        url: 'https://www.bls.gov/charts/employment-situation/civilian-unemployment-rate.htm',
        quote:
          'The unemployment rate in February 2020 was 3.5%, matching the lowest rate since 1969.',
        date: '2020-03-15',
      },
      {
        name: 'Congressional Research Service',
        url: 'https://crsreports.congress.gov/product/pdf/R/R46554',
        quote:
          'The COVID-19 pandemic caused one of the largest economic shocks in modern history.',
        date: '2021-01-12',
      },
    ],
    category: 'Economy',
  },
  {
    timestamp: 78,
    speaker: 'Candidate B',
    claim: 'Our administration created 3 million new jobs.',
    rating: 'False',
    explanation:
      'Official data shows that approximately 1.5 million jobs were created during this period, not 3 million as claimed.',
    sources: [
      {
        name: 'Department of Labor Economic Reports',
        url: 'https://www.dol.gov/general/topic/statistics/employment',
        quote: 'Net job creation during the specified period was 1.53 million.',
        date: '2021-06-30',
      },
    ],
    category: 'Economy',
  },
  {
    timestamp: 142,
    speaker: 'Candidate A',
    claim: 'We passed the largest infrastructure bill in history.',
    rating: 'True',
    explanation:
      'The $1.2 trillion infrastructure bill passed in 2021 is indeed the largest investment in infrastructure in U.S. history when not adjusted for inflation.',
    sources: [
      {
        name: 'Congressional Budget Office',
        url: 'https://www.cbo.gov/publication/57406',
        quote:
          'The Infrastructure Investment and Jobs Act provides $1.2 trillion in funding, making it the largest infrastructure investment in American history.',
        date: '2021-11-15',
      },
      {
        name: 'White House Briefing',
        url: 'https://www.whitehouse.gov/briefing-room/statements-releases/2021/11/06/fact-sheet-the-bipartisan-infrastructure-deal/',
        quote:
          "The Bipartisan Infrastructure Deal is the largest investment in our nation's infrastructure in history.",
        date: '2021-11-06',
      },
    ],
    category: 'Infrastructure',
  },
  {
    timestamp: 195,
    speaker: 'Candidate B',
    claim: 'Crime rates have increased by 40% in the last year.',
    rating: 'False',
    explanation:
      'FBI data shows that while certain categories of crime increased in some cities, the overall national crime rate increased by approximately 5%, not 40%.',
    sources: [
      {
        name: 'FBI Uniform Crime Report',
        url: 'https://www.fbi.gov/services/cjis/ucr',
        quote:
          'The overall violent crime rate increased by 5.2% compared to the previous year.',
        date: '2021-09-27',
      },
      {
        name: 'Bureau of Justice Statistics',
        url: 'https://bjs.ojp.gov/library/publications/criminal-victimization-2020',
        quote: 'Property crime decreased by 8.1% from 2019 to 2020.',
        date: '2021-10-01',
      },
    ],
    category: 'Crime',
  },
  {
    timestamp: 240,
    speaker: 'Candidate A',
    claim: 'Our healthcare plan will cover all pre-existing conditions.',
    rating: 'Misleading',
    explanation:
      'While the proposed plan does include coverage for pre-existing conditions, it removes certain protections that currently exist, potentially making such coverage more expensive for some individuals.',
    sources: [
      {
        name: 'Congressional Budget Office Health Policy Analysis',
        url: 'https://www.cbo.gov/topics/health-care',
        quote:
          'The plan maintains coverage for pre-existing conditions but allows insurers to charge higher premiums based on health status in certain circumstances.',
        date: '2021-05-24',
      },
      {
        name: 'Kaiser Family Foundation',
        url: 'https://www.kff.org/health-reform/issue-brief/pre-existing-condition-protections/',
        quote:
          'The proposal would weaken protections by allowing states to permit insurers to charge higher premiums to people with pre-existing conditions.',
        date: '2021-04-15',
      },
    ],
    category: 'Healthcare',
  },
  {
    timestamp: 310,
    speaker: 'Candidate B',
    claim: 'Tax cuts have primarily benefited the top 1%.',
    rating: 'True',
    explanation:
      'Economic analysis shows that approximately 83% of the benefits from the tax cuts went to the top 1% of income earners.',
    sources: [
      {
        name: 'Tax Policy Center',
        url: 'https://www.taxpolicycenter.org/publications/distributional-analysis-conference-agreement-tax-cuts-and-jobs-act',
        quote:
          'By 2027, 82.8% of the tax cuts will accrue to the top 1% of income earners.',
        date: '2020-01-30',
      },
      {
        name: 'Congressional Research Service',
        url: 'https://crsreports.congress.gov/product/pdf/R/R45736',
        quote:
          'The distribution of benefits from the TCJA was heavily weighted toward higher-income taxpayers.',
        date: '2021-02-12',
      },
    ],
    category: 'Taxes',
  },
  {
    timestamp: 375,
    speaker: 'Candidate A',
    claim: 'Our border policy has reduced illegal crossings by 75%.',
    rating: 'False',
    explanation:
      'Border Patrol data indicates a 12% reduction in illegal border crossings during the specified period, not 75%.',
    sources: [
      {
        name: 'U.S. Customs and Border Protection',
        url: 'https://www.cbp.gov/newsroom/stats/southwest-land-border-encounters',
        quote:
          'Southwest border encounters decreased by 12% from the previous fiscal year.',
        date: '2021-10-22',
      },
      {
        name: 'Department of Homeland Security',
        url: 'https://www.dhs.gov/immigration-statistics',
        quote:
          'The total number of apprehensions decreased from 851,508 to 749,613, representing a 12% decrease.',
        date: '2021-11-01',
      },
    ],
    category: 'Immigration',
  },
  {
    timestamp: 430,
    speaker: 'Candidate B',
    claim: 'We have the highest incarceration rate in the world.',
    rating: 'True',
    explanation:
      'The United States does have the highest incarceration rate globally, with approximately 639 prisoners per 100,000 people.',
    sources: [
      {
        name: 'World Prison Brief',
        url: 'https://www.prisonstudies.org/highest-to-lowest/prison_population_rate',
        quote:
          'The United States has an incarceration rate of 639 per 100,000 population, the highest in the world.',
        date: '2021-05-01',
      },
      {
        name: 'Bureau of Justice Statistics',
        url: 'https://bjs.ojp.gov/library/publications/correctional-populations-united-states-2019',
        quote:
          'At year-end 2019, an estimated 2,068,800 persons were incarcerated in the United States.',
        date: '2021-07-15',
      },
    ],
    category: 'Criminal Justice',
  },
  {
    timestamp: 490,
    speaker: 'Candidate A',
    claim: 'Renewable energy now accounts for 40% of our energy production.',
    rating: 'False',
    explanation:
      'According to the U.S. Energy Information Administration, renewable energy sources account for approximately 20% of electricity generation, not 40%.',
    sources: [
      {
        name: 'U.S. Energy Information Administration',
        url: 'https://www.eia.gov/tools/faqs/faq.php?id=427&t=3',
        quote:
          'In 2020, renewable energy sources accounted for about 19.8% of electricity generation in the United States.',
        date: '2021-03-05',
      },
      {
        name: 'National Renewable Energy Laboratory',
        url: 'https://www.nrel.gov/analysis/re-futures.html',
        quote:
          'The share of renewables in the U.S. electricity generation mix has grown from 10% in 2010 to 20% in 2020.',
        date: '2021-06-22',
      },
    ],
    category: 'Energy',
  },
  {
    timestamp: 540,
    speaker: 'Candidate B',
    claim:
      'Our education plan will increase teacher salaries by an average of $10,000.',
    rating: 'Misleading',
    explanation:
      'While the plan does allocate funding for teacher salary increases, the actual average increase would be closer to $4,500 based on current budget projections and state matching requirements.',
    sources: [
      {
        name: 'Department of Education Budget Analysis',
        url: 'https://www2.ed.gov/about/overview/budget/index.html',
        quote:
          'The proposed budget would provide grants to states that could increase teacher salaries by an average of $4,500 if fully implemented.',
        date: '2021-04-10',
      },
      {
        name: 'National Education Association',
        url: 'https://www.nea.org/resource-library/educator-pay-and-student-spending-how-does-your-state-rank',
        quote:
          'The proposed federal funding would cover approximately 45% of the promised salary increases.',
        date: '2021-05-18',
      },
    ],
    category: 'Education',
  },
  {
    timestamp: 615,
    speaker: 'Candidate A',
    claim: "We've reduced the federal deficit by $400 billion.",
    rating: 'True',
    explanation:
      'Treasury Department data confirms that the federal deficit was reduced by $406 billion during the specified fiscal year.',
    sources: [
      {
        name: 'U.S. Treasury Department',
        url: 'https://fiscal.treasury.gov/reports-statements/',
        quote:
          'The federal deficit decreased from $2.8 trillion to $2.4 trillion, a reduction of $406 billion.',
        date: '2022-01-15',
      },
      {
        name: 'Congressional Budget Office',
        url: 'https://www.cbo.gov/topics/budget',
        quote:
          'The federal deficit for fiscal year 2021 was $406 billion lower than in fiscal year 2020.',
        date: '2022-01-10',
      },
    ],
    category: 'Economy',
  },
  {
    timestamp: 680,
    speaker: 'Candidate B',
    claim:
      'The average family is paying $5,000 more per year due to inflation.',
    rating: 'Misleading',
    explanation:
      'While inflation has increased costs for families, the $5,000 figure is an overestimate. Economic analysis suggests the actual impact is closer to $2,500 for the median household.',
    sources: [
      {
        name: 'Bureau of Labor Statistics',
        url: 'https://www.bls.gov/cpi/',
        quote:
          'The Consumer Price Index rose 7.5% for the 12 months ending January 2022.',
        date: '2022-02-10',
      },
      {
        name: 'Joint Economic Committee',
        url: 'https://www.jec.senate.gov/public/index.cfm/republicans/analysis',
        quote:
          'The average American household spent approximately $2,500 more in 2021 due to inflation.',
        date: '2022-01-25',
      },
    ],
    category: 'Economy',
  },
  {
    timestamp: 750,
    speaker: 'Candidate A',
    claim:
      '95% of Americans support universal background checks for gun purchases.',
    rating: 'Misleading',
    explanation:
      'While polls consistently show high support for universal background checks, the actual percentage varies between 84-92% depending on the poll, not quite 95%.',
    sources: [
      {
        name: 'Gallup Poll',
        url: 'https://news.gallup.com/poll/1645/guns.aspx',
        quote:
          '92% of Americans favor requiring background checks for all gun sales.',
        date: '2021-08-05',
      },
      {
        name: 'Pew Research Center',
        url: 'https://www.pewresearch.org/politics/2021/04/20/amid-a-series-of-mass-shootings-in-the-u-s-gun-policy-remains-deeply-divisive/',
        quote:
          '84% of adults say they favor making private gun sales and sales at gun shows subject to background checks.',
        date: '2021-04-20',
      },
    ],
    category: 'Gun Policy',
  },
  {
    timestamp: 820,
    speaker: 'Candidate B',
    claim: 'Our state has the highest graduation rate in the country at 94%.',
    rating: 'False',
    explanation:
      "According to Department of Education data, the state's graduation rate is 88%, which ranks 5th in the nation, not 1st.",
    sources: [
      {
        name: 'Department of Education',
        url: 'https://nces.ed.gov/programs/coe/indicator/coi',
        quote:
          "The state's adjusted cohort graduation rate is 88.1%, ranking 5th nationally.",
        date: '2021-05-01',
      },
      {
        name: 'National Center for Education Statistics',
        url: 'https://nces.ed.gov/fastfacts/display.asp?id=805',
        quote: 'The highest state graduation rate is 91.4%, achieved by Iowa.',
        date: '2021-05-01',
      },
    ],
    category: 'Education',
  },
  {
    timestamp: 890,
    speaker: 'Candidate A',
    claim: "We've built 300 miles of new border wall.",
    rating: 'Misleading',
    explanation:
      'While approximately 300 miles of barriers were constructed, only about 80 miles were in locations where no barriers previously existed. The rest replaced existing barriers.',
    sources: [
      {
        name: 'U.S. Customs and Border Protection',
        url: 'https://www.cbp.gov/border-security/along-us-borders/border-wall-system',
        quote:
          'As of January 2021, 452 miles of border wall system has been completed, with approximately 80 miles constructed in locations where no barriers previously existed.',
        date: '2021-01-22',
      },
      {
        name: 'Government Accountability Office',
        url: 'https://www.gao.gov/products/gao-21-114',
        quote:
          'The majority of construction replaced existing barriers rather than adding barriers to new locations.',
        date: '2021-02-15',
      },
    ],
    category: 'Immigration',
  },
  {
    timestamp: 960,
    speaker: 'Candidate B',
    claim:
      'The average American pays more for prescription drugs than citizens of any other country.',
    rating: 'True',
    explanation:
      'OECD data confirms that Americans pay the highest prices for prescription medications among developed nations.',
    sources: [
      {
        name: 'OECD Health Statistics',
        url: 'https://www.oecd.org/health/health-statistics.htm',
        quote:
          'The United States spends more per capita on prescription drugs than any other OECD country.',
        date: '2021-07-10',
      },
      {
        name: 'RAND Corporation',
        url: 'https://www.rand.org/news/press/2021/01/28.html',
        quote:
          'Prescription drug prices in the United States are 2.56 times higher than those in other OECD countries.',
        date: '2021-01-28',
      },
    ],
    category: 'Healthcare',
  },
  {
    timestamp: 1020,
    speaker: 'Candidate A',
    claim:
      'Our trade deficit with China has decreased by 50% under my administration.',
    rating: 'False',
    explanation:
      'Census Bureau data shows that the trade deficit with China decreased by approximately 18%, not 50%.',
    sources: [
      {
        name: 'U.S. Census Bureau',
        url: 'https://www.census.gov/foreign-trade/balance/c5700.html',
        quote:
          'The U.S. trade deficit with China decreased from $375.2 billion in 2017 to $310.8 billion in 2020, a reduction of 17.2%.',
        date: '2021-02-05',
      },
      {
        name: 'Bureau of Economic Analysis',
        url: 'https://www.bea.gov/data/intl-trade-investment/international-trade-goods-and-services',
        quote:
          'The overall U.S. trade deficit actually increased during this period, despite the modest decrease in the deficit with China.',
        date: '2021-03-05',
      },
    ],
    category: 'Trade',
  },
  {
    timestamp: 1085,
    speaker: 'Candidate B',
    claim: 'Our plan will create 10 million clean energy jobs.',
    rating: 'Unverified',
    explanation:
      'This claim is a projection that cannot be verified currently. Economic models from independent analysts suggest the plan could create between 2-7 million jobs, but 10 million appears to be an optimistic estimate.',
    sources: [
      {
        name: 'Economic Policy Institute',
        url: 'https://www.epi.org/publication/green-jobs-analysis/',
        quote:
          'Based on our analysis, the proposed clean energy investments could create between 2.2 and 4.5 million jobs over the next decade.',
        date: '2021-09-05',
      },
      {
        name: 'Princeton University Net-Zero America Project',
        url: 'https://netzeroamerica.princeton.edu/',
        quote:
          'The transition to net-zero emissions could create 2-6 million energy supply jobs by 2050, depending on the pathway chosen.',
        date: '2021-10-29',
      },
    ],
    category: 'Energy',
  },
  {
    timestamp: 1150,
    speaker: 'Candidate A',
    claim: 'Violent crime has decreased every year of my administration.',
    rating: 'False',
    explanation:
      'FBI crime statistics show that violent crime increased in two of the four years of the administration.',
    sources: [
      {
        name: 'FBI Uniform Crime Report',
        url: 'https://www.fbi.gov/services/cjis/ucr',
        quote:
          'Violent crime increased by 3.3% in 2018 and 5.6% in 2020 compared to the previous years.',
        date: '2021-09-27',
      },
      {
        name: 'Bureau of Justice Statistics',
        url: 'https://bjs.ojp.gov/library/publications/criminal-victimization-2020',
        quote:
          'The violent crime rate increased from 1.15 victimizations per 1,000 persons age 12 or older in 2019 to 1.33 in 2020.',
        date: '2021-10-01',
      },
    ],
    category: 'Crime',
  },
  {
    timestamp: 1210,
    speaker: 'Candidate B',
    claim: "We've reduced carbon emissions by 30% since 2005.",
    rating: 'True',
    explanation:
      'EPA data confirms that U.S. greenhouse gas emissions were approximately 30% lower in 2020 compared to 2005 levels.',
    sources: [
      {
        name: 'Environmental Protection Agency',
        url: 'https://www.epa.gov/ghgemissions/inventory-us-greenhouse-gas-emissions-and-sinks',
        quote:
          'U.S. greenhouse gas emissions decreased by 30.2% from 2005 to 2020.',
        date: '2021-04-14',
      },
      {
        name: 'Energy Information Administration',
        url: 'https://www.eia.gov/environment/emissions/carbon/',
        quote:
          'Energy-related CO2 emissions in 2020 were 31% below their 2005 level.',
        date: '2021-04-20',
      },
    ],
    category: 'Environment',
  },
  {
    timestamp: 1280,
    speaker: 'Candidate A',
    claim:
      "We've eliminated more regulations than any administration in history.",
    rating: 'Misleading',
    explanation:
      'While the administration did reduce a significant number of regulations, the claim of "more than any administration" is difficult to verify due to inconsistent historical record-keeping and differing definitions of "regulation."',
    sources: [
      {
        name: 'Office of Management and Budget',
        url: 'https://www.whitehouse.gov/omb/information-regulatory-affairs/',
        quote:
          'The administration eliminated 178 significant regulatory actions during its term.',
        date: '2021-01-15',
      },
      {
        name: 'Brookings Institution',
        url: 'https://www.brookings.edu/research/tracking-deregulation-in-the-trump-era/',
        quote:
          'While substantial deregulation occurred, comparing across administrations is problematic due to differing metrics and historical record-keeping.',
        date: '2021-01-20',
      },
    ],
    category: 'Regulation',
  },
  {
    timestamp: 1340,
    speaker: 'Candidate B',
    claim: '50 million Americans have no health insurance.',
    rating: 'False',
    explanation:
      'According to Census Bureau data, approximately 28 million Americans lacked health insurance in 2020, not 50 million.',
    sources: [
      {
        name: 'U.S. Census Bureau',
        url: 'https://www.census.gov/library/publications/2021/demo/p60-274.html',
        quote:
          '28.0 million people (8.6% of the population) did not have health insurance at any point during 2020.',
        date: '2021-09-14',
      },
      {
        name: 'Kaiser Family Foundation',
        url: 'https://www.kff.org/uninsured/issue-brief/key-facts-about-the-uninsured-population/',
        quote:
          'In 2020, 28.0 million nonelderly individuals were uninsured, a decrease from 46.5 million in 2010.',
        date: '2021-11-06',
      },
    ],
    category: 'Healthcare',
  },
  {
    timestamp: 1410,
    speaker: 'Candidate A',
    claim: 'The stock market has hit record highs 100 times during my term.',
    rating: 'True',
    explanation:
      'Market data confirms that the Dow Jones Industrial Average reached record highs 103 times during the specified period.',
    sources: [
      {
        name: 'Dow Jones Market Data',
        url: 'https://www.wsj.com/market-data',
        quote:
          'The Dow Jones Industrial Average hit 103 record closing highs during the four-year period.',
        date: '2021-01-20',
      },
      {
        name: 'S&P Dow Jones Indices',
        url: 'https://www.spglobal.com/spdji/en/',
        quote:
          'The S&P 500 set 145 new record closing highs during the same period.',
        date: '2021-01-20',
      },
    ],
    category: 'Economy',
  },
  {
    timestamp: 1470,
    speaker: 'Candidate B',
    claim: 'The wealthiest 1% pay a lower tax rate than the middle class.',
    rating: 'Misleading',
    explanation:
      'While some ultra-wealthy individuals may pay lower effective tax rates due to capital gains and various deductions, IRS data shows that the top 1% as a group pays a higher average effective tax rate than middle-income earners.',
    sources: [
      {
        name: 'Internal Revenue Service',
        url: 'https://www.irs.gov/statistics/soi-tax-stats-individual-income-tax-rates-and-tax-shares',
        quote:
          'The top 1% of income earners paid an average effective federal income tax rate of 25.4%, compared to 13.6% for the middle quintile.',
        date: '2021-09-30',
      },
      {
        name: 'Tax Policy Center',
        url: 'https://www.taxpolicycenter.org/statistics/historical-average-federal-tax-rates-all-households',
        quote:
          'When accounting for all federal taxes, the top 1% paid an average rate of 30.2%, compared to 16.1% for the middle quintile.',
        date: '2021-08-15',
      },
    ],
    category: 'Taxes',
  },
]
